import { assets } from "@core/assets";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import type { IconType } from "react-icons";
import Button from "../../Button";
import { cn } from "@core/utils/cn";
import { dropdownPanelStyles } from "../helper";
import useClickOutside from "@core/utils/clickOutside/useClickOutside";

type MenuOption = {
  name: string;
  key?: string;
  label?: string;
  Icon?: IconType | string | undefined;
  iconPosition?: "left" | "right";
  iconClassName?: string;
  action?: () => void;
  className?: string;
};

type DropdownMenuProps = {
  options: MenuOption[];
  position?:
    | "bottomright"
    | "bottomleft"
    | "bottomcenter"
    | "topright"
    | "topleft"
    | "topcenter";
  icon?: IconType | string;
  iconClassName?: string;
  className?: string;
  currentOption?: string | undefined;
};

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const IconMenuOptions = ({
  options,
  position = "bottomright",
  icon: CustomIcon,
  iconClassName = "text-xl",
  className = "",
  currentOption,
}: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({
    position: "fixed",
    top: 0,
    left: 0,
  });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const IconComponent = CustomIcon || (assets["more-vertical"] as IconType);

  const isOptionSelected = (option: MenuOption) =>
    !!currentOption && !!option.key && currentOption === option.key;

  // Close on outside click (menu + trigger)
  useClickOutside([triggerRef, menuRef], () => {
    if (isOpen) setIsOpen(false);
  });

  const compute = () => {
    const t = triggerRef.current;
    const m = menuRef.current;
    if (!t || !m) return;

    const tr = t.getBoundingClientRect();
    const mr = m.getBoundingClientRect();

    const gap = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = position.startsWith("top")
      ? tr.top - mr.height - gap
      : tr.bottom + gap;

    let left: number;
    if (position.endsWith("right")) left = tr.right - mr.width;
    else if (position.endsWith("center"))
      left = tr.left + tr.width / 2 - mr.width / 2;
    else left = tr.left;

    // clamp horizontally
    if (left + mr.width > vw - gap) left = vw - mr.width - gap;
    if (left < gap) left = gap;

    // flip vertically if needed
    if (!position.startsWith("top") && top + mr.height > vh - gap) {
      top = tr.top - mr.height - gap;
    }
    if (position.startsWith("top") && top < gap) {
      top = tr.bottom + gap;
    }

    // final clamp
    if (top + mr.height > vh - gap) top = vh - mr.height - gap;
    if (top < gap) top = gap;

    setMenuStyle({ position: "fixed", top, left });
  };

  useIsomorphicLayoutEffect(() => {
    if (!isOpen) return;

    setReady(false);

    requestAnimationFrame(() => {
      compute();
      setReady(true);
    });

    const onAnyScroll = () => compute();
    window.addEventListener("resize", compute, { passive: true });
    window.addEventListener("scroll", onAnyScroll, true);

    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", onAnyScroll, true);
    };
  }, [isOpen, position]);

  return (
    <div className="inline-block">
      <Button
        ref={triggerRef}
        variant="text"
        size={"sm"}
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className={`${className} active:hover:bg-transparent text-neutral-500`}
        aria-label="Open menu"
        aria-expanded={isOpen}
      >
        <IconComponent className={iconClassName} />
      </Button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            style={{
              ...menuStyle,
              visibility: ready ? "visible" : "hidden",
            }}
            className={cn(dropdownPanelStyles(), "z-[10000]")}
            role="menu"
          >
            {options.map((option, index) => {
              const selected = isOptionSelected(option);

              return (
                <div key={option.key ?? option.name ?? index}>
                  <button
                    type="button"
                    onClick={() => {
                      option.action?.();
                      setIsOpen(false);
                    }}
                    className={cn(
                      "group flex w-full items-center gap-2 px-3 py-2 text-left transition-colors rounded-md",
                      selected ? "bg-neutral-50" : "hover:bg-neutral-50",
                    )}
                  >
                    {option.Icon && (
                      <option.Icon
                        className={cn(
                          "h-4 w-4",
                          option.iconPosition === "right" && "order-last",
                          option.iconClassName ?? "text-neutral-400",
                        )}
                      />
                    )}

                    <div className="min-w-0">
                      <div
                        className={cn(
                          "capitalize text-xs truncate",
                          selected
                            ? "text-primary-800 font-medium"
                            : (option.className ?? "text-neutral-950"),
                        )}
                      >
                        {option.name}
                      </div>

                      {option.label && (
                        <div className="text-xs text-neutral-400 mt-0.5 truncate">
                          {option.label}
                        </div>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default IconMenuOptions;
